
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Lock, Trash2 } from "lucide-react";
import { Room } from "@/hooks/useRooms";
import { useDevices, useDeleteDevice, useUpdateDevice } from "@/hooks/useDevices";
import AddDeviceDialog from "./AddDeviceDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

interface RoomDevicesDialogProps {
  room: Room;
  providerId: string;
  onDevicesUpdated: () => void;
}

const RoomDevicesDialog = ({ room, providerId, onDevicesUpdated }: RoomDevicesDialogProps) => {
  const [open, setOpen] = useState(false);
  const { data: devices, refetch: refetchDevices } = useDevices(providerId);
  const deleteDevice = useDeleteDevice();
  const updateDevice = useUpdateDevice();

  const roomDevices = devices?.filter(device => device.room_id === room.id) || [];

  const handleDeviceAdded = () => {
    refetchDevices();
    onDevicesUpdated();
  };

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      await deleteDevice.mutateAsync(deviceId);
      refetchDevices();
      onDevicesUpdated();
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  const handleToggleDevice = async (deviceId: string, currentEnabled: boolean) => {
    try {
      await updateDevice.mutateAsync({
        id: deviceId,
        is_enabled: !currentEnabled
      });
      refetchDevices();
      onDevicesUpdated();
    } catch (error) {
      console.error('Error toggling device:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-center">
          <Lock className="h-4 w-4 mr-2" />
          Devices ({roomDevices.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Access Devices - {room.name}</DialogTitle>
          <DialogDescription>
            Manage door access control devices for this room. Devices enable self-service entry for customers.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h4 className="text-sm font-medium">Current Devices</h4>
            <AddDeviceDialog 
              providerId={providerId}
              room={room}
              onDeviceAdded={handleDeviceAdded}
            />
          </div>

          {roomDevices.length > 0 ? (
            <div className="space-y-4">
              {roomDevices.map((device) => (
                <div key={device.id} className="flex flex-col gap-4 p-4 border rounded-lg">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Lock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="font-medium break-all">{device.device_name}</span>
                      <Badge variant="secondary" className="flex-shrink-0 text-xs">
                        {device.seam_device_id ? 'Connected' : 'Local'}
                      </Badge>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <Badge 
                            variant={device.is_enabled ? 'default' : 'destructive'} 
                            className="flex-shrink-0 cursor-help text-xs"
                          >
                            {device.is_enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80">
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold">Device Status</h4>
                            <p className="text-sm text-gray-600">
                              {device.is_enabled 
                                ? "This device is currently active and can be used for customer check-ins."
                                : "This device is disabled and cannot be used for access control. Customers will not receive access codes for this device."
                              }
                            </p>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="break-all">Device ID: {device.device_id}</p>
                      {device.device_pin && (
                        <p>PIN: {device.device_pin}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`device-${device.id}`}
                        checked={device.is_enabled}
                        onCheckedChange={() => handleToggleDevice(device.id, device.is_enabled)}
                        disabled={updateDevice.isPending}
                      />
                      <label 
                        htmlFor={`device-${device.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {device.is_enabled ? 'Enabled' : 'Disabled'}
                      </label>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 w-full sm:w-auto">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove Device
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Device</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove "{device.device_name}"? This will disable access control for this device.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteDevice(device.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Remove Device
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Lock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No devices configured for this room</p>
              <p className="text-sm">Add a device to enable self-service access</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoomDevicesDialog;
